import { useCallback, useEffect, useState } from "react";
import {
  createManufacturingVendor,
  deleteManufacturingVendor,
  listManufacturingVendors,
  updateManufacturingVendor,
  type MfgVendor,
} from "@/lib/manufacturing/purchaseOrderClient";

export function useManufacturingVendorsTab(reloadTick: number) {
  const [vendors, setVendors] = useState<MfgVendor[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setBusy(true);
    setError(null);
    const res = await listManufacturingVendors();
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    const rows = Array.isArray(res.data.vendors) ? res.data.vendors : [];
    // Guard against the alias-typeahead shape accidentally hitting this client.
    setVendors(rows.filter((v) => v && typeof v === "object" && typeof v.id === "string"));
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, reloadTick]);

  const create = useCallback(
    async (payload: {
      name: string;
      primary_contact_name?: string;
      phone?: string;
      email?: string;
      address?: string;
      extra_contacts?: MfgVendor["extra_contacts"];
    }) => {
      setBusy(true);
      setError(null);
      const res = await createManufacturingVendor(payload);
      setBusy(false);
      if (!res.ok) {
        setError(res.error);
        return false;
      }
      setToast(`Created vendor ${res.data.vendor.name}`);
      await reload();
      return true;
    },
    [reload]
  );

  const update = useCallback(
    async (payload: {
      vendor_id: string;
      name?: string;
      primary_contact_name?: string;
      phone?: string;
      email?: string;
      address?: string;
      extra_contacts?: MfgVendor["extra_contacts"];
      is_active?: boolean;
    }) => {
      setBusy(true);
      setError(null);
      const res = await updateManufacturingVendor(payload);
      setBusy(false);
      if (!res.ok) {
        setError(res.error);
        return false;
      }
      setToast(`Updated vendor ${res.data.vendor.name}`);
      await reload();
      return true;
    },
    [reload]
  );

  const remove = useCallback(
    async (vendorId: string) => {
      setBusy(true);
      setError(null);
      const res = await deleteManufacturingVendor({ vendor_id: vendorId });
      setBusy(false);
      if (!res.ok) {
        setError(res.error);
        return false;
      }
      setToast("Vendor deleted.");
      await reload();
      return true;
    },
    [reload]
  );

  return { vendors, busy, error, toast, setToast, reload, create, update, remove };
}
